import React, { useLayoutEffect, useRef } from "react";
import L from "leaflet";
import {
  disableMapNavigation,
  createInfoboxControl,
  createMap,
  createControls,
  createGlobalZoom,
} from "./map-utils";
import { MapConfig, Position } from "./types";

export interface MapProps {
  maps: MapConfig[];
  mapRef: React.MutableRefObject<L.Map | undefined>;
  infoRef: React.MutableRefObject<any | undefined>;
  controlsRef: React.MutableRefObject<any | undefined>;
  geoJsonRef: React.MutableRefObject<any | undefined>;
  initialFocus: Position;
  initialZoom: number;
  withTileLayer?: boolean;
  children: any;
  isStatic: boolean;
  data: any;
  selectedMap: MapConfig;
  onChange: (option: MapConfig) => void;
  onClick?: () => void;
  idPrefix?: string;
  hasValidLocation: boolean;
}

export function Map({
  maps,
  mapRef,
  initialFocus,
  initialZoom,
  isStatic,
  data,
  infoRef,
  children,
  geoJsonRef,
  controlsRef,
  onChange,
  selectedMap,
  onClick,
  idPrefix,
  hasValidLocation,
}: MapProps) {
  const globalZoomRef = useRef();

  const prefix = idPrefix ? `${idPrefix}-` : "";
  const id = isStatic ? `${prefix}static-map` : `${prefix}map`;

  const classNames = `leaflet-map-container ${
    isStatic ? "leaflet-map-container--static" : ""
  }`;
  useLayoutEffect(() => {
    createMap({ mapRef, isStatic, initialFocus, initialZoom, id });
    if (isStatic) {
      disableMapNavigation({ map: mapRef.current! });
    }
  }, [id, initialFocus, initialZoom, isStatic, mapRef]);

  useLayoutEffect(() => {
    if (!isStatic) {
      createInfoboxControl({
        multipleMaps: maps.length > 1,
        selectedMap,
        infoRef,
        mapRef,
        data,
      });
    }
  }, [data, infoRef, isStatic, mapRef, maps.length, selectedMap]);

  useLayoutEffect(() => {
    if (!isStatic) {
      createControls({
        maps,
        controlsRef,
        mapRef,
        geoJsonRef,
        onChange,
        selectedMap,
        infoRef,
      });
    }
    // if we add all dependencies, when we change the selected map, it duplicates the map controls
    // eslint-disable-next-line
  }, [controlsRef, geoJsonRef, infoRef, isStatic, mapRef]);

  useLayoutEffect(() => {
    if (!isStatic)
      createGlobalZoom({
        focus: initialFocus,
        globalZoomRef,
        mapRef,
        hasValidLocation,
      });
  }, [hasValidLocation, initialFocus, isStatic, mapRef]);

  return (
    <div id={id} className={classNames} onClick={onClick}>
      <>{children}</>
    </div>
  );
}
